import { type ReactNode } from 'react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { cn } from '@/lib/utils';
import { useSources } from '../lib/api';
import type { SourceCatalogDto, SourceType } from '../types';

// Tab 7 — Sources. The complete source catalog behind every WWD fact (principle: every
// fact carries its source). Paywalled rows are catalog entries only — never scraped.

const TYPE_ORDER: SourceType[] = ['government', 'drillco', 'operator', 'media', 'analytics', 'other'];

const TYPE_LABELS: Record<SourceType, string> = {
  government: 'Government / regulators',
  drillco: 'Drilling contractors',
  operator: 'Operators',
  media: 'Trade media',
  analytics: 'Analytics',
  other: 'Other',
};

const TYPE_BADGE_LABELS: Record<SourceType, string> = {
  government: 'Government',
  drillco: 'Drill Co.',
  operator: 'Operator',
  media: 'Media',
  analytics: 'Analytics',
  other: 'Other',
};

type Access = SourceCatalogDto['access'];

const ACCESS_STYLES: Record<Access, string> = {
  free: 'border-border text-muted',
  registration: 'border-accent-info text-accent-info',
  paywalled: 'border-accent-warn text-accent-warn',
};

export default function Sources() {
  const { data, isLoading, error } = useSources();

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error || !data) return <ErrorState message={(error as Error | null)?.message} />;

  const { sources } = data;

  const groups = TYPE_ORDER.map((type) => ({
    type,
    rows: sources.filter((s) => s.type === type),
  })).filter((g) => g.rows.length > 0);

  return (
    <div className="stagger">
      <Card>
        <CardTitle>Sources</CardTitle>
        <CardDescription className="mt-2">
          Every fact in WWD carries its source. This catalog is the complete source list.
        </CardDescription>
      </Card>

      <Card>
        {sources.length === 0 ? (
          <CardDescription>No sources cataloged yet.</CardDescription>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-accent">
                  <Th>Name</Th>
                  <Th>Type</Th>
                  <Th>Access</Th>
                  <Th center>RSS</Th>
                  <Th>Used for</Th>
                </tr>
              </thead>
              {groups.map((g) => (
                <tbody key={g.type}>
                  <tr>
                    <th
                      scope="rowgroup"
                      colSpan={5}
                      className="pt-5 pb-2 px-3 text-left font-(family-name:--font-ui) text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted"
                    >
                      {TYPE_LABELS[g.type]}
                    </th>
                  </tr>
                  {g.rows.map((s) => (
                    <SourceRow key={s.slug} source={s} />
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Th({ children, center }: { children: ReactNode; center?: boolean }) {
  return (
    <th
      className={cn(
        'py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)',
        center ? 'text-center' : 'text-left'
      )}
    >
      {children}
    </th>
  );
}

function Badge({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap rounded-(--radius-sm) border bg-surface-elevated px-2 py-0.5',
        'font-(family-name:--font-ui) text-[0.65rem] font-medium uppercase tracking-[0.12em]',
        className
      )}
    >
      {children}
    </span>
  );
}

function SourceRow({ source }: { source: SourceCatalogDto }) {
  return (
    <tr className="border-b border-border align-top hover:bg-surface-hover">
      <td className="py-3 px-3">
        <a
          href={source.url}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-accent hover:underline"
        >
          {source.name}
        </a>
      </td>
      <td className="py-3 px-3">
        <Badge className="border-border text-muted">{TYPE_BADGE_LABELS[source.type]}</Badge>
      </td>
      <td className="py-3 px-3">
        <Badge className={ACCESS_STYLES[source.access]}>{source.access}</Badge>
      </td>
      <td className="py-3 px-3 text-center">
        {source.rssUrl ? (
          <span
            className="inline-block h-2 w-2 rounded-full bg-score-high"
            title={'RSS feed: ' + source.rssUrl}
            aria-label="RSS feed available"
            role="img"
          />
        ) : (
          <span className="text-muted" aria-label="No RSS feed">
            —
          </span>
        )}
      </td>
      <td className="py-3 px-3 text-muted">{source.usedFor}</td>
    </tr>
  );
}
